import type { PackageOffer } from '../../graphql/queries/subscriptionPackages';

type OfferPricing = Pick<PackageOffer, 'billingModel' | 'priceOneTime' | 'priceMonthly' | 'priceWeekly'>;

const money = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

/** "$10 one-off" / "$99 a month" / "$20 a week". */
export function offerPrice(pkg: OfferPricing): string {
  if (pkg.billingModel === 'ONE_TIME') return `${money(pkg.priceOneTime ?? 0)} one-off, no auto-renew`;
  return pkg.priceMonthly > 0 ? `${money(pkg.priceMonthly)} a month` : `${money(pkg.priceWeekly)} a week`;
}

/** "Access until Sunday 8 November" for a one-off with a fixed last day, else null. */
export function accessEndsLabel(pkg: Pick<PackageOffer, 'accessEndsOn'>): string | null {
  if (!pkg.accessEndsOn || !/^\d{4}-\d{2}-\d{2}$/.test(pkg.accessEndsOn)) return null;
  // midday UTC on the day, formatted in UTC: the calendar day never shifts
  const day = new Date(`${pkg.accessEndsOn}T12:00:00Z`).toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  });
  return `Access until ${day}`;
}

/** "Sales close Monday 2 November, 9:00 pm AEDT" when the offer has a sale end, else null. */
export function saleEndsLabel(pkg: Pick<PackageOffer, 'saleEndsAt'>): string | null {
  if (!pkg.saleEndsAt) return null;
  const at = new Date(pkg.saleEndsAt);
  if (Number.isNaN(at.getTime())) return null;
  const opts = { timeZone: 'Australia/Melbourne' } as const;
  const day = at.toLocaleDateString('en-AU', { ...opts, weekday: 'long', day: 'numeric', month: 'long' });
  const time = at.toLocaleTimeString('en-AU', { ...opts, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  return `Sales close ${day}, ${time}`;
}
